import { onlineManager, useSuspenseQuery } from '@tanstack/react-query'
import { Outlet, createFileRoute } from '@tanstack/react-router'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { CheckoutFormFieldSchema } from '@/schemas/booking'
import KohBusCheckout from '@/components/make-booking/checkout'
import { queryKeys } from '@/queries/keys'
import {
  getBookingPlanQueryOptions,
  getBookingQueryOptions,
} from '@/queries/query'
import { BOOKING_STEPS } from '@/utils/constant'
import useStepMutation from '@/hooks/useStepMutation'
import ModalLeaveBooking from '@/components/ui/ModalLeaveBooking'

export const Route = createFileRoute('/make-booking/$id/checkout')({
  loader: ({ context }) => {
    if (onlineManager.isOnline() && !context.isRestoring) {
      return context.queryClient.ensureQueryData(getBookingPlanQueryOptions())
    }
    return context.queryClient.getQueryData(queryKeys.booking.plan())
  },
  component: RouteComponent,
})

function RouteComponent() {
  const isAuthenticated = Route.useRouteContext({
    select: (s) => s.authContext.isAuthenticated,
  })
  const bookingId = Route.useParams({
    select: (s) => s.id,
  })
  const {
    data: {
      data: { booking },
    },
  } = useSuspenseQuery(getBookingQueryOptions(bookingId))
  const form = useForm<CheckoutFormFieldSchema>({
    mode: 'all',
    resolver: zodResolver(CheckoutFormFieldSchema),
    defaultValues: {
      ...booking,
      contact_name: booking.contact_name ?? '',
      contact_email: booking.contact_email ?? '',
      contact_phone: booking.contact_phone ?? '',
      is_poc_same_with_contact_info:
        booking.is_poc_same_with_contact_info ?? false,
      poc_name: booking.poc_name ?? '',
      poc_phone: booking.poc_phone ?? '',
    },
  })
  const { update } = useStepMutation()

  useEffect(() => {
    update.mutate({
      id: bookingId,
      step: BOOKING_STEPS.STEP_CHECKOUT,
      type: 'booking',
    })
  }, [])

  return (
    <FormProvider {...form}>
      <KohBusCheckout />
      <Outlet />
      <ModalLeaveBooking
        isAuthenticated={isAuthenticated}
        disabledBlock={!!booking.parent}
      />
    </FormProvider>
  )
}
